import React from 'react';
import {Link} from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import '../../css/Homepage.css';

const Homepage = () => {
  const {auth} = useAuth();
  const signedIn = Boolean(auth?.user);
  const primaryLink = signedIn ? '/app/dashboard' : '/login';
  const primaryLabel = signedIn ? 'Przejdź do panelu' : 'Zaloguj się';

  const features = [
    {
      icon: 'fa-calendar-alt',
      title: 'Grafiki i dostępność',
      text: 'Planuj obsadę, dostępność i zmiany w jednym czytelnym środowisku.'
    },
    {
      icon: 'fa-users',
      title: 'Kadry i HR',
      text: 'Kartoteki pracowników, umowy, dokumenty, absencje, certyfikaty i historia zatrudnienia.'
    },
    {
      icon: 'fa-clock',
      title: 'RCP i czas pracy',
      text: 'Porównuj plan z wykonaniem i analizuj wejścia, wyjścia, spóźnienia oraz przepracowane godziny.'
    },
    {
      icon: 'fa-chart-line',
      title: 'Koszty i planowanie',
      text: 'Łącz zapotrzebowanie, roboczogodziny, koszty pracy i dane sprzedażowe w jednym procesie.'
    },
    {
      icon: 'fa-user-check',
      title: 'Portal pracownika',
      text: 'Grafik, nieobecności, wnioski, powiadomienia i najważniejsze informacje dostępne dla pracownika.'
    },
    {
      icon: 'fa-graduation-cap',
      title: 'Szkolenia i wiedza',
      text: 'Zarządzaj szkoleniami, ocenami, testami i firmową bazą wiedzy zależnie od roli użytkownika.'
    }
  ];

  return (
    <main className="qstaff-landing">
      <section className="qstaff-landing-hero">
        <div className="qstaff-landing-backdrop" />
        <nav className="qstaff-landing-nav">
          <Link className="qstaff-landing-brand" to="/" aria-label="Qstaff - strona główna">
            <span className="qstaff-brand-mark">Q</span>
            <span className="qstaff-brand-name">QSTAFF</span>
          </Link>
          <div className="qstaff-landing-nav-links">
            <a href="#mozliwosci">Możliwości</a>
            <a href="#jak-dziala">Jak działa</a>
            <Link className="qstaff-nav-login" to={primaryLink}>{primaryLabel}</Link>
          </div>
        </nav>

        <div className="qstaff-landing-hero-content">
          <div className="qstaff-landing-copy">
            <div className="qstaff-eyebrow">
              <span />
              Zarządzanie personelem bez chaosu
            </div>
            <h1>Twój zespół.<br/>Jeden system.</h1>
            <p>
              Qstaff łączy planowanie grafików, kadry, czas pracy, szkolenia i kontrolę kosztów,
              żeby decyzje operacyjne były szybsze i oparte na aktualnych danych.
            </p>
            <div className="qstaff-hero-actions">
              <Link className="qstaff-primary-cta" to={primaryLink}>
                {primaryLabel}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>
              <a className="qstaff-secondary-cta" href="#mozliwosci">Poznaj Qstaff</a>
            </div>
            <div className="qstaff-hero-checks">
              <span><i className="fas fa-check-circle" /> Grafiki i dostępność</span>
              <span><i className="fas fa-check-circle" /> Kadry i HR</span>
              <span><i className="fas fa-check-circle" /> RCP i analityka</span>
            </div>
          </div>

          <div className="qstaff-dashboard-preview" aria-label="Podgląd możliwości Qstaff">
            <div className="qstaff-preview-topbar">
              <div>
                <span className="qstaff-preview-kicker">PANEL OPERACYJNY</span>
                <strong>Dzisiaj w Qstaff</strong>
              </div>
              <span className="qstaff-live"><i /> AKTUALNE</span>
            </div>

            <div className="qstaff-preview-stats">
              <div>
                <span>Zaplanowana obsada</span>
                <strong>86</strong>
                <small><i className="fas fa-users" /> pracowników</small>
              </div>
              <div>
                <span>Roboczogodziny</span>
                <strong>642 h</strong>
                <small><i className="fas fa-clock" /> plan dnia</small>
              </div>
              <div>
                <span>Pokrycie planu</span>
                <strong>96%</strong>
                <small className="qstaff-good"><i className="fas fa-arrow-up" /> stabilnie</small>
              </div>
            </div>

            <div className="qstaff-preview-panel">
              <div className="qstaff-preview-panel-head">
                <div>
                  <strong>Grafik zmianowy</strong>
                  <span>Podgląd obsady działów</span>
                </div>
                <span>Poniedziałek</span>
              </div>
              <div className="qstaff-schedule-row">
                <span>Gastronomia</span>
                <div><b style={{width: '88%'}} /></div>
                <strong>24 / 26</strong>
              </div>
              <div className="qstaff-schedule-row">
                <span>Atrakcje</span>
                <div><b style={{width: '100%'}} /></div>
                <strong>31 / 31</strong>
              </div>
              <div className="qstaff-schedule-row">
                <span>Kasy</span>
                <div><b style={{width: '94%'}} /></div>
                <strong>15 / 16</strong>
              </div>
              <div className="qstaff-schedule-row">
                <span>Biuro</span>
                <div><b style={{width: '100%'}} /></div>
                <strong>8 / 8</strong>
              </div>
            </div>

            <div className="qstaff-preview-alert">
              <i className="fas fa-bolt" />
              <div>
                <strong>Decyzje w jednym miejscu</strong>
                <span>Grafik, koszty, dostępność i RCP pracują na wspólnych danych.</span>
              </div>
            </div>
          </div>
        </div>

        <div className="qstaff-hero-bottom">
          <span>Planowanie</span>
          <i />
          <span>Kadry</span>
          <i />
          <span>RCP</span>
          <i />
          <span>Szkolenia</span>
          <i />
          <span>Raporty</span>
        </div>
      </section>

      <section className="qstaff-features" id="mozliwosci">
        <div className="qstaff-section-heading">
          <span className="qstaff-section-label">MOŻLIWOŚCI</span>
          <h2>Wszystko, czego potrzebujesz do zarządzania zespołem</h2>
          <p>Od planowania pracy po analizę wykonania — bez rozproszonych arkuszy i ręcznego przepisywania danych.</p>
        </div>
        <div className="qstaff-feature-grid">
          {features.map(feature => (
            <article className="qstaff-feature-card" key={feature.title}>
              <div className="qstaff-feature-icon"><i className={`fas ${feature.icon}`} /></div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="qstaff-workflow" id="jak-dziala">
        <div className="qstaff-workflow-inner">
          <div className="qstaff-workflow-copy">
            <span className="qstaff-section-label">JAK DZIAŁA</span>
            <h2>Od zapotrzebowania do rozliczenia w jednym procesie</h2>
            <p>
              Qstaff porządkuje cały cykl planowania pracy. Każdy etap korzysta z tych samych danych,
              więc zmiana w jednym miejscu nie wymaga aktualizowania kilku niezależnych zestawień.
            </p>
            <Link className="qstaff-text-link" to={primaryLink}>
              Otwórz aplikację <i className="fas fa-arrow-right" />
            </Link>
          </div>
          <div className="qstaff-workflow-steps">
            <div className="qstaff-workflow-step">
              <span>01</span>
              <div><strong>Określ zapotrzebowanie</strong><p>Zaplanuj obsadę, godziny i wymagania dla działów.</p></div>
            </div>
            <div className="qstaff-workflow-step">
              <span>02</span>
              <div><strong>Ułóż grafik</strong><p>Uwzględnij dostępność, stanowiska i warunki pracy.</p></div>
            </div>
            <div className="qstaff-workflow-step">
              <span>03</span>
              <div><strong>Opublikuj i komunikuj</strong><p>Przekaż pracownikom aktualny plan i obsłuż zmiany.</p></div>
            </div>
            <div className="qstaff-workflow-step">
              <span>04</span>
              <div><strong>Porównaj plan z wykonaniem</strong><p>Połącz RCP, godziny i koszty w raportach.</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="qstaff-final-cta">
        <div>
          <span className="qstaff-section-label">QSTAFF</span>
          <h2>Więcej kontroli nad personelem.<br/>Mniej pracy administracyjnej.</h2>
        </div>
        <Link className="qstaff-primary-cta qstaff-primary-cta-light" to={primaryLink}>
          {primaryLabel}
          <i className="fas fa-arrow-right" aria-hidden="true" />
        </Link>
      </section>

      <footer className="qstaff-landing-footer">
        <Link className="qstaff-landing-brand qstaff-footer-brand" to="/">
          <span className="qstaff-brand-mark">Q</span>
          <span className="qstaff-brand-name">QSTAFF</span>
        </Link>
        <span>System zarządzania personelem</span>
      </footer>
    </main>
  );
};

export default Homepage;
